<template>
    <div class="step-content unlayer-email-broadcast-audience-step-content">
        <slot name="title" />

        <div class="main-content">
            <div v-show="active" class="email-builder-input-container">
                <div v-if="isCustomizeToAddressInBroadcastsEnabled" class="email-to-field">
                    <ds-multiselect
                        v-model="toAddress"
                        categorized
                        class="sender-address-field"
                        placeholder="Email Type"
                        data-qa="to-address-field"
                        label-prop="label"
                        label-value="value"
                        :options="audienceOptions"
                        @input="handleEmailTypeChange"
                    />
                </div>
                <ds-multiselect
                    class="input-field email-builder-multiselect"
                    data-qa="recipient-search"
                    help-text
                    help-text-prop="labelSubtext"
                    label-prop="label"
                    multiple
                    required
                    searchable
                    value-prop="id"
                    :force-invalid="error"
                    :label="$t('email.broadcast.steps.audience.recipientsLabel')"
                    :options="availableRecipientsOptions"
                    :placeholder="$t('global.to')"
                    :value="recipients"
                    @input="handleRecipientsChange"
                    @updateSearch="searchRecipients"
                >
                    <template #selection="{ option, remove }">
                        <ds-chip
                            class="recipient-chip"
                            remove
                            with-interaction
                            @mousedown.native.stop="remove"
                        >
                            <template #leading>
                                <ds-icon :name="getOptionIcon(option)" />
                            </template>

                            <template #default>
                                {{ option.name || option.label }} - {{ getOptionCount(option) }}
                            </template>
                        </ds-chip>
                    </template>
                </ds-multiselect>

                <div class="recipients-description">
                    <span v-if="error" class="recipients-hint invalid">
                        {{ this.$t('email.broadcast.steps.audience.recipientsHintError') }}
                    </span>

                    <template v-else>
                        <span class="recipients-hint only-desktop">
                            {{ this.$t('email.broadcast.steps.audience.recipientsHintVerbose') }}
                        </span>

                        <span class="recipients-hint only-mobile">
                            {{ this.$t('email.broadcast.steps.audience.recipientsHintBrief') }}
                        </span>
                    </template>

                    <span
                        class="total-recipients"
                        :class="{ 'invalid': error }"
                    >
                        {{ recipientsMessage }}
                    </span>
                </div>

                <ds-outline-button
                    class="save"
                    :loading="isUpdatingRecipientCount"
                    @click.stop="save"
                >
                    {{ $t('global.save') }}
                </ds-outline-button>

                <ds-text-button
                    variant="text"
                    @click="cancel"
                >
                    {{ $t('global.cancel') }}
                </ds-text-button>
            </div>

            <EmailBroadcastStepOverview v-show="!active" :sections="overviewSections" />
        </div>

        <slot name="action" :actionText="$t('email.broadcast.steps.audience.actionText')" />
    </div>
</template>

<script>
import clonedeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';

import EmailBroadcastStepOverview from './EmailBroadcastStepOverview';

const BASE_TO_EMAIL_OPTIONS = [
    { category: { label: 'Email', value: '[[contact.email]]' } },
    { category: { label: 'Email 2', value: '[[contact.email2.address]]' } },
    { category: { label: 'Email 3', value: '[[contact.email3.address]]' } },
];

const SEARCHING_DEBOUNCE_DELAY = 300;

const AUTOCOMPLETE_ICONS = {
    LIST: 'list',
    USER: 'user',
    TAG: 'tag',
};

const AUTOCOMPLETE_ICON_BY_CATEGORY = {
    LIST: AUTOCOMPLETE_ICONS.LIST,
    SAVEDSEARCH: AUTOCOMPLETE_ICONS.LIST,
    TAG: AUTOCOMPLETE_ICONS.TAG,
    SINGLECONTACTTAG: AUTOCOMPLETE_ICONS.USER,
    LEADSOURCE: AUTOCOMPLETE_ICONS.LIST,
    STAGE: AUTOCOMPLETE_ICONS.LIST,
    PRODUCT: AUTOCOMPLETE_ICONS.LIST,
};

import {
    unlayerBroadcastsSearchEmailRecipients as searchEmailRecipients,
    unlayerBroadcastsGetRecipientCountV2 as getRecipientCountV2,
} from '@/api/broadcasts/emailBroadcast';

import { STEPS, STEP_ERRORS } from '@/components/unlayer/constants/editor.constants';

import {
    differenceById,
    validateBroadcast,
} from '../../utils/emailBroadcast.util';

const SYNCING_DEBOUNCE_DELAY = 1000;

export default {
    name: 'EmailBroadcastAudienceStep',

    components: {
        EmailBroadcastStepOverview,
    },

    props: {
        active: Boolean,
        broadcast: {
            type: Object,
            required: true,
        },
        showErrors: Boolean,
        isSavingBroadcast: Boolean,
        isRecalculatingRecipientStatistics: Boolean,
        isCustomizeToAddressInBroadcastsEnabled: {
            type: Boolean,
        },
        mergeFields: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            error: false,
            isCalculatingRecipients: false,
            isRecipientsSyncRequired: false,
            isUpdatingRecipientCount: false,
            recipientCounts: {},
            recipientStatistics: {},
            recipients: [],
            recipientsOptions: [],
            toAddress: [],
            audienceOptions: [],
        };
    },

    created() {
        this.searchRecipients = debounce(this.searchRecipients, SEARCHING_DEBOUNCE_DELAY);
        this.searchRecipients('');
        this.syncData = debounce(this.syncData, SYNCING_DEBOUNCE_DELAY);
        this.retrieveAudienceOptions();
    },

    watch: {
        'broadcast.emailBroadcastRecipientStatistics': {
            handler() {
                this.syncData();
            },
            deep: true,
            immediate: true,
        },

        showErrors(val) {
            if (val) this.validate();
        },

        error(val) {
            if (val && this.recipients.length > 0) {
                // Emit error to parent only if this step is in NEW state
                // And this step will only be in NEW state if there are no recipients
                this.$emit('error', STEP_ERRORS.NO_ELIGIBLE_RECIPIENTS);
            }
        },
    },

    computed: {
        availableRecipientsOptions() {
            const result = differenceById(
                (this.recipientsOptions ?? []).map((o) => ({ ...o, id: `${o.id}` })),
                this.recipients,
            ).map((option) => ({
                ...option,
                icon: this.getOptionIcon(option),
            }));

            return result;
        },

        eligibleRecipientsMessage() {
            const { numEligibleRecipients } = this;

            return this.$tc('email.broadcast.steps.audience.eligibleRecipients', numEligibleRecipients, { count: numEligibleRecipients.toLocaleString() });
        },

        isNumEligibleRecipientsKnown() {
            return typeof this.broadcast?.emailBroadcastRecipientStatistics?.eligibleRecipients === 'number';
        },

        hasRecipients() {
            const { recipientCounts, numTotalRecipients } = this;

            return Object.keys(recipientCounts).length > 0 && numTotalRecipients > 0;
        },

        hasNoEligibleRecipients() {
            const { isRecipientsSyncRequired } = this;
            const totalRecipients = this.broadcast?.emailBroadcastRecipientStatistics?.totalRecipients;
            const eligibleRecipients = this.broadcast?.emailBroadcastRecipientStatistics?.eligibleRecipients;

            return !isRecipientsSyncRequired && totalRecipients > 0 && eligibleRecipients === 0;
        },

        isSavedBroadcast() {
            const { broadcast: { id } } = this;

            return !!id;
        },

        numEligibleRecipients() {
            return this.broadcast?.emailBroadcastRecipientStatistics?.eligibleRecipients ?? 0;
        },

        numTotalRecipients() {
            return Object.values(this.recipientCounts).reduce((total, count) => count + total, 0);
        },

        numTotalRecipientsMessage() {
            const { numTotalRecipients } = this;

            return this.$tc(
                'email.broadcast.steps.audience.totalRecipients',
                numTotalRecipients,
                { count: numTotalRecipients.toLocaleString() },
            );
        },

        overviewSections() {
            const {
                hasRecipients,
                isSavedBroadcast,
                isSavingBroadcast,
                isNumEligibleRecipientsKnown,
                eligibleRecipientsMessage,
                numTotalRecipientsMessage,
            } = this;

            let content;
            let placeholder = this.$t('email.broadcast.steps.audience.placeholder');

            if (isSavingBroadcast || this.isRecalculatingRecipientStatistics) {
                placeholder = this.$t('email.broadcast.steps.audience.calculatingRecipients');
                content = null;
            } else if (isSavedBroadcast && hasRecipients) {
                content = isNumEligibleRecipientsKnown ? eligibleRecipientsMessage : numTotalRecipientsMessage;
            } else {
                content = null;
            }

            return [{
                title: this.$t('global.to'),
                content,
                placeholder,
            }];
        },

        recipientsMessage() {
            const {
                eligibleRecipientsMessage,
                isNumEligibleRecipientsKnown,
                isRecipientsSyncRequired,
                numTotalRecipientsMessage,
                isSavingBroadcast,
                isUpdatingRecipientCount,
            } = this;

            if (isSavingBroadcast || isUpdatingRecipientCount) {
                return this.$t('email.broadcast.steps.audience.calculatingRecipients');
            }

            let msg;

            // cannot merge if and else conditions as order matters here
            if (isRecipientsSyncRequired) {
                msg = numTotalRecipientsMessage;
            } else if (isNumEligibleRecipientsKnown) {
                msg = eligibleRecipientsMessage;
            } else {
                msg = numTotalRecipientsMessage;
            }

            return msg;
        },
    },

    methods: {
        cancel() {
            this.$emit('cancel');
        },

        getOptionCount(option) {
            return (this.recipientCounts[option.id] ?? 0).toLocaleString();
        },

        getOptionIcon({ category, type }) {
            return AUTOCOMPLETE_ICON_BY_CATEGORY[category || type];
        },

        async handleRecipientsChange(updatedRecipients) {
            this.isRecipientsSyncRequired = true;

            // clear error on update
            this.error = false;

            // this will only be called with one diff, either add or remove
            const { recipients: existingRecipients } = this;

            /**
             * Because multiselect does emit add/remove events for selections
             * we must diff the changes determine the change
             */

            const addedRecipients = differenceById(updatedRecipients, existingRecipients);
            const removedRecipients = differenceById(existingRecipients, updatedRecipients);

            this.recipients = updatedRecipients;

            if (addedRecipients.length > 0) {
                await this.updateRecipientCount(addedRecipients[0]);
            }

            if (removedRecipients.length > 0) {
                this.removeRecipientCount(removedRecipients[0]);
            }
        },

        removeRecipientCount({ id }) {
            this.recipientCounts[id] = 0;
        },

        save() {
            if (this.isCustomizeToAddressInBroadcastsEnabled) {
                const { recipients, toAddress } = this;

                this.validate();

                if (!this.error) {
                    this.$emit('save', { recipients, toAddress: toAddress.value });
                }
            } else {
                const { recipients } = this;

                this.validate();

                if (!this.error) {
                    this.$emit('save', { recipients });
                }
            }
        },

        async searchRecipients(query) {
            try {
                const results = await searchEmailRecipients({ query });

                this.recipientsOptions = results;
            } catch (error) {
                this.recipientsOptions = [];
            }
        },

        async syncData() {
            this.isCalculatingRecipients = true;

            if (!isEqual(this.broadcast.recipients, this.recipients)) {
                this.isRecipientsSyncRequired = true;
            }

            if (this.isRecipientsSyncRequired) {
                await this.syncRecipients();
                this.validate();
                this.isRecipientsSyncRequired = false;
            }

            this.recipientStatistics = { ...this.broadcast?.emailBroadcastRecipientStatistics };

            if (this.hasNoEligibleRecipients) {
                this.validate();
            }

            this.isCalculatingRecipients = false;
        },

        async syncRecipients() {
            const { recipients } = this.broadcast;
            const ids = recipients.map(({ id }) => id);

            this.recipients = clonedeep(recipients);

            await Promise.all(recipients.map(this.updateRecipientCount));

            for (const id of Object.keys(this.recipientCounts)) {
                // if there any recipient counts not in this new list, reset to 0
                // We could just reset the object, but it causes a temporary flicker to 0 on the screen
                if (!ids.includes(id)) {
                    this.recipientCounts[id] = 0;
                }
            }
        },

        async updateRecipientCount(recipient) {
            this.isUpdatingRecipientCount = true;

            try {
                const recipientCount = await getRecipientCountV2(recipient);

                this.$set(this.recipientCounts, recipient.id, Number(recipientCount.toString().replace(/\D/g, '')));
            } catch (err) {
                this.$error({ message: this.$t('email.broadcast.steps.audience.errors.getRecipientCount') });
            }

            this.isUpdatingRecipientCount = false;
        },

        validate() {
            const { hasNoEligibleRecipients, numTotalRecipients, recipients } = this;

            this.error = hasNoEligibleRecipients
                || numTotalRecipients === 0
                || !validateBroadcast({ recipients }, STEPS.AUDIENCE);
        },

        retrieveToAddress() {
            // normalize toAddress
            let normalizedToAddress = this.broadcast.template?.toAddress;

            if (normalizedToAddress) {
                normalizedToAddress = normalizedToAddress
                    .replace(/~(.*?)~/g, '[[$1]]')
                    .toLowerCase();
            }

            // assign email type
            try {
                const [selectedAudience] = this.audienceOptions.flatMap((item) => {
                    if (item.category.value === normalizedToAddress) {
                        return item.category;
                    }

                    if (item.options) {
                        return item.options.find((option) => option.value === normalizedToAddress);
                    }

                    return null;
                }).filter((item) => item);

                this.toAddress = selectedAudience;
            } catch (error) {
                this.toAddress = clonedeep(BASE_TO_EMAIL_OPTIONS)[0].category;
            }
        },
        async retrieveAudienceOptions() {
            if (this.isCustomizeToAddressInBroadcastsEnabled) {
                this.audienceOptions = clonedeep(BASE_TO_EMAIL_OPTIONS);

                try {
                    const newOptions = this.mergeFields
                        .filter((element) => element.value === 'customFields')
                        .flatMap((category) => category.fields.filter((field) => field.type === 'Email'))
                        .map((customField) => {
                            return {
                                label: customField.label,
                                value: customField.value,
                            };
                        });

                    if (newOptions.length > 0) {
                        this.audienceOptions.push({ category: { label: 'Custom Fields', value: 'customFields' }, options: newOptions });
                    }
                    this.retrieveToAddress();
                } catch (error) {
                    // do nothing
                }
            }
        },

        async handleEmailTypeChange() {
            this.isRecipientsSyncRequired = true;

            // clear error on update
            this.error = false;
        },
    },
};
</script>

<style lang="scss" scoped>
@import './steps';

.input-field {
    margin-bottom: px-to-rem(4);
    border: var(--multiselect-minwidth, 100%);
}

.recipients-description {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: $spacing-200;

    :last-child {
        margin-left: $spacing-200;
    }

    &.invalid {
        color: $color-red
    }
}

.recipients-hint,
.total-recipients  {
    font-size: $font-size-xs;
    color: $color-text-subtle;
    white-space: nowrap;

    &.invalid {
        color: $color-red
    }
}

.email-to-field {
    display:grid;
    grid-template-columns: 2fr 3fr;
}
</style>
